/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { logger } from '@adornis/base/logging.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/legacy/x-data-view.js';
import '@adornis/entity-picker/client/x-entity-picker.js';
import { download } from '@adornis/file-utils/client/helper-methods.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { SubscriptionPagination } from '@adornis/pagination/paginatable.js';
import type { AdornisUser } from '@adornis/users/db/a-user.js';
import { Product } from '@chancenwerk/shared/_cms/products/entities/Product.js';
import { ProductLKK } from '@chancenwerk/shared/_cms/products/entities/ProductLKK.js';
import '@chancenwerk/client/components/chancen-component-card-title.js';
import type { ChromiumButton } from '@chancenwerk/client/design/components/cr-button.js';
import { Adresse } from '@chancenwerk/shared/adresse.js';
import { Course } from '@chancenwerk/shared/courses/Course.js';
import { CourseDigital } from '@chancenwerk/shared/courses/CourseDigital.js';
import { ExportType } from '@chancenwerk/shared/enums/AttendanceExportType.js';
import { AttendanceExportEventlog } from '@chancenwerk/shared/eventlogs/AttendanceExportEventlog.js';
import { ChancenwerkUserFilter } from '@chancenwerk/shared/filter/ChancenwerkUserFilter.js';
import { CourseFilter } from '@chancenwerk/shared/filter/CourseFilter.js';
import { KpfFilter } from '@chancenwerk/shared/filter/KpfFilter.js';
import {
  PresenceRecord,
  Status,
  UserPresenceRecord,
  UserPresenceRecordFilter,
} from '@chancenwerk/shared/PresenceRecord.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import '@chancenwerk/client/design/components/cr-checkbox.js';
import '@chancenwerk/client/design/components/cr-dropdown-multiselection.js';
import '@chancenwerk/client/design/components/cr-dropdown-selection.js';
import '@chancenwerk/client/design/components/cr-loader.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import '@chancenwerk/client/design/components/cr-progress-bar.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import { KPFAPI } from '@chancenwerk/shared/master-data-management/kpf/api/KPFAPI.js';
import { ProductAspect } from '@chancenwerk/shared/master-data-management/kpf/entities/aspects/ProductAspect.js';
import { UserAPI } from '@chancenwerk/shared/master-data-management/users/api/UserAPI.js';
import { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import { ExternerMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/ExternerMitarbeiter.js';
import { Mitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/Mitarbeiter.js';
import { Student } from '@chancenwerk/shared/master-data-management/users/entities/Student.js';
import { TemporaryUser } from '@chancenwerk/shared/master-data-management/users/entities/TemporaryUser.js';
import { VerwaltungsMitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { combineLatest, filter, merge, type Observable, of, switchMap, takeUntil } from 'rxjs';
import type { KPF } from '../../../../src/master-data-management/kpf/entities/KPF.js';
import { attendanceColor, attendanceIcon } from '../components/board-component-attendance-card.js';
import '../components/board-component-course-digital-card.js';
import '../components/board-component-kpf-filter.js';

const courseSelector: BaseQLSelectionSet<Course> = {
  _id: 1,
  _class: 1,
  name: 1,
  kpfID: 1,
  location: 1,
  weekday: 1,
  startTime: 1,
  endTime: 1,
  leKoIDs: 1,
  osStudentIDs: 1,
  usStudentIDs: 1,
  schuKoIDs: 1,
  startDate: 1,
  plannedStartDate: 1,
};

const kpfSelector: BaseQLSelectionSet<KPF> = {
  _id: 1,
  _class: 1,
  kuerzel: 1,
  school: { name: 1, kuerzel: 1, address: Adresse.allFields },
  produkt: Product.allFields,
};

const userSelector: BaseQLSelectionSet<ChancenwerkUser | Student> = {
  _class: 1,
  _id: 1,
  vorname: 1,
  nachname: 1,
  email: 1,
  geschlecht: 1,
  schuelertyp: 1,
  guestAccount: 1,
};

const studentSelector: BaseQLSelectionSet<Student> = {
  ...userSelector,
  schoolInformation: { _class: 1, jahrgangsstufe: 1, zug: 1 },
  buts: { BuTBerechtigung: 1, BuTBewilligungAb: 1, BuTBewilligungBis: 1 },
  joinInformation: { eintrittsdatum: 1 },
  kuendigung: { austrittsdatum: 1 },
};

const userRecordSelector: BaseQLSelectionSet<UserPresenceRecord> = {
  _id: 1,
  _class: 1,
  userID: 1,
  date: 1,
  status: 1,
  comment: 1,
  excuseReason: 1,
  user: userSelector,
};

const recordSelector: BaseQLSelectionSet<PresenceRecord> = {
  _id: 1,
  _class: 1,
  date: 1,
  course: { _id: 1, _class: 1, osStudentIDs: 1, usStudentIDs: 1, leKoIDs: 1, schuKoIDs: 1 },
  comment: 1,
  courseID: 1,
  schukoRecords: { _id: 1, _class: 1, kpfID: 1, date: 1, schukoRecords: userRecordSelector },
  studentRecordsOS: userRecordSelector,
  studentRecordsUS: userRecordSelector,
  lekoRecords: userRecordSelector,
};

/**
 * @customElement board-page-attendance-export
 */
@customElement('board-page-attendance-export')
export class BoardPageCourseDigital extends CrPage {
  override styles() {
    return [...super.styles(), { ':host': { height: '100%' } }];
  }

  constructor() {
    super();
    this.label = 'Anwesenheit Export';
    this.description = 'Hier kannst du die Anwesenheiten von Kursen exportieren.';
    this.permission = ChancenPermission.board.EXPORT_ATTENDANCE;
  }

  private readonly _kpfsPaginated = new SubscriptionPagination<KPF, KpfFilter[]>(
    KPFAPI.subscribeByFilter,
    [
      new KpfFilter({
        aspects: [
          new BooleanAspect({ customID: 'active', value: true }),
          new ProductAspect({ values: [ProductLKK._class] }),
        ],
      }),
    ],
    kpfSelector,
    15,
  );

  @state() private readonly _userSearch = new RXController<Maybe<string>>(this, null);
  @state() private readonly _kpfsByUser = new RXController(
    this,
    this._userSearch.observable.pipe(
      switchMap(search => {
        if (!search) {
          return of([] as KPF[]);
        }

        return KPFAPI.subscribeByAttendance(0, 100, search)(kpfSelector);
      }),
    ),
  );
  @state() private readonly _kpfs = new RXController(this, this._kpfsPaginated.data);
  @state() private readonly _selectedKpf = new RXController<KPF | null>(this, null);
  @state() private readonly _filter = new FormController(
    this,
    new UserPresenceRecordFilter({
      hasBuTFrom: DateTime.now().startOf('month').toJSDate(),
      hasBuTTill: DateTime.now().endOf('month').toJSDate(),
    }),
  );
  @state() private readonly _type = new RXController(this, ExportType.SCHUKO_LEKO);

  @state() private readonly _extraCourses = new RXController(this, [] as Course[]);
  @state() private readonly _coursesFromKPF = new RXController(
    this,
    combineLatest([this._selectedKpf.observable, this._extraCourses.observable]).pipe(
      switchMap(([kpf, extraCourses]) => {
        const courses = kpf ? Course.subscribeAllCoursesIncludingArchivedByKPFID(kpf._id)(courseSelector) : of([]);
        return merge(courses, of(extraCourses));
      }),
    ),
  );

  private readonly _selectedRecords = new RXController(
    this,
    combineLatest([this._coursesFromKPF.observable, this._filter.document!.whenChanging]).pipe(
      switchMap(async values => {
        const courses = values[0];
        const from = values[1].hasBuTFrom ?? DateTime.now().startOf('month');
        const to = values[1].hasBuTTill ?? DateTime.now().endOf('month');

        if (!courses) {
          return null;
        }

        const records: PresenceRecord[] = [];
        for (const c of courses) {
          const pr = await PresenceRecord.getPresenceRecordForCourseAndDateRange(c._id, from, to)(recordSelector);
          if (!pr) {
            continue;
          }

          records.push(...pr);
        }

        return records.sort((a, b) => a.date.toUnixInteger() - b.date.toUnixInteger());
      }),
    ),
  );

  private readonly _selectedRecordsByKey = new RXController(
    this,
    this._selectedRecords.observable.pipe(
      switchMap(async records => {
        const recordsByKey = new Map<string, PresenceRecord[]>();
        if (!records) {
          return recordsByKey;
        }
        records.forEach(r => {
          const key = r.key;
          if (!recordsByKey.has(key)) {
            recordsByKey.set(key, []);
          }
          recordsByKey.get(key)?.push(r);
        });
        return recordsByKey;
      }),
    ),
  );

  @state() private readonly _students = new RXController(
    this,
    this._selectedRecords.observable.pipe(
      switchMap(async records => {
        if (!records) {
          return null;
        }

        const from = this._filter.document?.hasBuTFrom ?? DateTime.now().startOf('month');

        const students = await PresenceRecord.getStudentsFromRecords(
          records.map(r => r._id),
          this._filter.document,
        )(studentSelector);

        const activeStudents = students.filter(s => {
          const austrittsdatum = s.kuendigung?.austrittsdatum;
          return !austrittsdatum || austrittsdatum >= from.startOf('day');
        });

        return activeStudents.sort((a, b) => `${a.vorname} ${a.nachname}`.localeCompare(`${b.vorname} ${b.nachname}`));
      }),
    ),
  );

  @state() private readonly _lekoSchuko = new RXController(
    this,
    this._selectedRecords.observable.pipe(
      switchMap(async records => {
        if (!records) {
          return null;
        }

        const lekoSchuko = await PresenceRecord.getSchuKoLeKosFromRecords(
          records.map(r => r._id),
          this._filter.document,
        )(userSelector);
        return lekoSchuko.sort((a, b) => `${a.vorname} ${a.nachname}`.localeCompare(`${b.vorname} ${b.nachname}`));
      }),
    ),
  );

  private get _datesFormatted() {
    return {
      from: this._filter.document?.hasBuTFrom?.toFormat('yyyy.dd.MM'),
      to: this._filter.document?.hasBuTTill?.toFormat('yyyy.dd.MM'),
    };
  }

  override connectedCallback() {
    super.connectedCallback();

    this._kpfsPaginated.loadingInitially.pipe(takeUntil(this.disconnected)).subscribe(() => {
      this.requestUpdate();
    });

    this._kpfsPaginated.updateParameters(
      new KpfFilter({
        aspects: [
          new BooleanAspect({ customID: 'active', value: true }),
          new ProductAspect({ values: [ProductLKK._class] }),
        ],
      }),
    );
  }

  override content() {
    return html` <cr-flex space="md" padding="xs">
      ${this._basicInformationSelection()}

      <cr-grid columns="400px 1fr" space="md">
        ${this._monthSelection()}
        ${this._selectedKpf.value
          ? html`<cr-card padding="md" space="md">
              <chancen-component-card-title>Filter</chancen-component-card-title>
              <cr-flex horizontal space="md" crossaxis-center>${this._typeFilter()}</cr-flex>
            </cr-card>`
          : nothing}
      </cr-grid>

      ${this._tableStudents()} ${this._tableLekoSchuko()} ${this._exportButton()}
    </cr-flex>`;
  }

  private _kpfSelection() {
    if (this._userSearch.value && this._userSearch.value.length > 0) {
      return html` ${this._kpfsByUser.isLoading
        ? html`<cr-flex crossaxis-center horizontal space="md">
            <cr-loader ${css({ width: '25px', height: '25px' })}></cr-loader>
            <cr-text ${css({ opacity: 0.5 })}>Suche KPFs...</cr-text>
          </cr-flex>`
        : html`<cr-dropdown-selection
            clearable
            .value=${this._selectedKpf.value}
            icon="search"
            placeholder=${this._kpfsByUser.value?.length === 0
              ? 'KPF'
              : `KPF (${this._kpfsByUser.value?.length} gefunden)`}
            .selectables=${this._kpfsByUser.value}
            .renderString=${(entry: Maybe<KPF>) => {
              if (!entry) return 'KPF auswählen';
              return entry.stringRepresentation;
            }}
            @value-picked=${(e: CustomEvent<{ value: KPF }>) => {
              this._selectedKpf.next(e.detail.value);
              this._extraCourses.next([]);
            }}
          ></cr-dropdown-selection>`}`;
    }
    return html` ${this._kpfsPaginated.loadingInitially.value
      ? html`<cr-flex crossaxis-center horizontal space="md">
          <cr-loader ${css({ width: '25px', height: '25px' })}></cr-loader>
          <cr-text ${css({ opacity: 0.5 })}>Suche KPFs...</cr-text>
        </cr-flex>`
      : html`<cr-dropdown-selection
          clearable
          .value=${this._selectedKpf.value}
          icon="search"
          placeholder=${this._kpfsPaginated.data.value?.length === 0
            ? 'KPF'
            : `KPF (${this._kpfsPaginated.data.value?.length} gefunden)`}
          .selectables=${this._kpfs.value}
          .renderString=${(entry: Maybe<KPF>) => {
            if (!entry) return 'KPF auswählen';
            return entry.stringRepresentation;
          }}
          @value-picked=${(e: CustomEvent<{ value: KPF }>) => {
            this._selectedKpf.next(e.detail.value);
            this._extraCourses.next([]);
          }}
        ></cr-dropdown-selection>`}`;
  }

  private _digitalCourseSelection() {
    return html` <x-entity-picker
      flex
      placeholder="Digitale Intensivkurse"
      clearable
      .value=${this._extraCourses.value[0]?._id}
      .options=${{
        class: CourseDigital,
        operation: (skip, limit, filter) => {
          return CourseDigital.subscribeCoursesByFilter(filter);
        },
        selectionSet: {
          name: 1,
          subject: 1,
          jahrgangsstufe: 1,
          weekday: 1,
          startTime: 1,
          endTime: 1,
          ...courseSelector,
        },
        filter: new CourseFilter({ searchFields: ['weekday', 'subject'], courseType: CourseDigital._class }),
      }}
      .renderString=${(e: Maybe<CourseDigital>) => {
        if (!e) {
          return '';
        }
        return `${e?.subject} ${e?.jahrgangsstufe} ${e?.weekday} (${e?.startTime?.toFormat(
          'HH:mm',
        )} - ${e?.endTime?.toFormat('HH:mm')})`;
      }}
      @entity-picked=${(e: ValueEvent<Maybe<CourseDigital>>) => {
        this._selectedKpf.next(null);
        if (!e.detail.value) {
          this._extraCourses.next([]);
          return;
        }

        this._type.next(ExportType.US_OS);
        this._extraCourses.next([e.detail.value]);
      }}
    ></x-entity-picker>`;
  }

  private _basicInformationSelection() {
    return html`<board-component-kpf-filter name="Filter nach KPF" .kpfsPaginated=${this._kpfsPaginated}>
      <chancen-component-card-title slot="front">Export Auswahl</chancen-component-card-title>
      <cr-nospace-toggle-content .label=${html`<cr-text>Filter nach Person</cr-text>`}>
        <x-entity-picker
          ${css({ maxWidth: '720px' })}
          placeholder="Person mit Anwesenheit"
          clearable
          .options=${{
            class: ChancenwerkUser,
            selectionSet: { vorname: 1, nachname: 1 },
            operation: UserAPI.subscribeByFilter,
            filter: new ChancenwerkUserFilter({
              userClass: [
                Mitarbeiter._class,
                VerwaltungsMitarbeiter._class,
                ExternerMitarbeiter._class,
                Student._class,
              ],
            }),
          }}
          @value-picked=${(e: ValueEvent<Maybe<string>>) => {
            this._userSearch.next(e.detail.value);
            if (!e.detail.value) {
              this._selectedKpf.next(null);
            }
          }}
        ></x-entity-picker>
      </cr-nospace-toggle-content>
      <cr-grid columns="1fr max-content 1fr 1fr" space="md" horizontal ${css({ alignItems: 'center' })}>
        ${this._kpfSelection()} <cr-text>oder</cr-text> ${this._digitalCourseSelection()}
        <cr-dropdown-selection
          placeholder="Typ"
          .value=${this._type.value}
          .selectables=${Object.values(ExportType)}
          @value-picked=${(e: ValueEvent<ExportType>) => this._type.next(e.detail.value)}
          ?disabled=${this._extraCourses.value.length > 0}
          ${css({ minWidth: '300px' })}
        ></cr-dropdown-selection>
      </cr-grid>
      ${(this._userSearch.value?.length ?? 0) > 0
        ? html`
            <cr-flex horizontal crossaxis-center space="sm" padding="sm">
              <cr-icon>info</cr-icon>
              <cr-text bold>
                Beachte: Solange nach einer Person gesucht wird, werden die 'Filter nach KPF' ignoriert.
              </cr-text>
            </cr-flex>
          `
        : nothing}
    </board-component-kpf-filter> `;
  }

  private _monthSelection() {
    return html`<cr-card space="md" padding="md">
      <chancen-component-card-title>Zeitraum</chancen-component-card-title>
      <cr-flex horizontal space="md">
        <cr-date-picker-dropdown
          .end-year=${DateTime.now().year}
          placeholder="Startdatum"
          ${this._filter.field('hasBuTFrom')}
          flex
          click-to-save
          clearable
          @value-picked="${(e: ValueEvent<DateTime>) => {
            if (!this._filter.document?.hasBuTFrom || !this._filter.document?.hasBuTTill) {
              return;
            }

            if (this._filter.document.hasBuTFrom > this._filter.document.hasBuTTill) {
              this._filter.document.hasBuTTill = this._filter.document.hasBuTFrom.endOf('month');
            }
          }}}"
          ${css({ maxWidth: '250px' })}
        ></cr-date-picker-dropdown>
        <cr-date-picker-dropdown
          .end-year=${DateTime.now().year}
          placeholder="Enddatum"
          ${this._filter.field('hasBuTTill')}
          flex
          click-to-save
          clearable
          ${css({ maxWidth: '250px' })}
        ></cr-date-picker-dropdown>
      </cr-flex>
    </cr-card>`;
  }

  private _typeFilter() {
    if (!this._selectedKpf.value) {
      return html`<cr-text>Bitte wähle einen KPF und Kurse aus.</cr-text>`;
    }

    switch (this._type.value) {
      case ExportType.SCHUKO_LEKO:
        return html`<cr-text>Keine Filter vorhanden</cr-text>`;
      case ExportType.US_OS:
        return html` <cr-checkbox ${this._filter.field('hasBuT')} label=${'Hat BuT bewilligung im Zeitraum'}>
        </cr-checkbox>`;
    }
    return nothing;
  }

  private _recordMapping(r: UserPresenceRecord[], user: AdornisUser) {
    const upr = r.find(sr => sr.userID === user._id && sr.status !== Status.NOT_SURE);
    if (!upr?.status) {
      return html`<cr-flex
        horizontal
        crossaxis-center
        space="sm"
        ${css({ color: attendanceColor(this, Status.NOT_SURE) })}
      >
        <cr-icon>${attendanceIcon(Status.NOT_SURE)}</cr-icon>
        <cr-text>${new UserPresenceRecord({ status: Status.NOT_SURE }).statusTranslated.toString()}</cr-text>
      </cr-flex>`;
    }

    return html`<cr-flex space="sm">
      <cr-flex horizontal crossaxis-center space="sm" ${css({ color: attendanceColor(this, upr.status) })}>
        <cr-icon>${attendanceIcon(upr.status)}</cr-icon>
        <cr-text>${upr.statusTranslated.toString()}</cr-text>
      </cr-flex>
      ${(upr.comment?.length ?? 0) > 0
        ? html`<cr-flex horizontal crossaxis-center space="sm">
            <cr-text bold>Kommentar: </cr-text> <cr-text>${upr.comment}</cr-text>
          </cr-flex>`
        : nothing}
      ${(upr.excuseReason?.length ?? 0) > 0
        ? html`<cr-flex horizontal crossaxis-center space="sm">
            <cr-text bold>Entschuldigung: </cr-text> <cr-text>${upr.excuseReason}</cr-text>
          </cr-flex>`
        : nothing}
    </cr-flex>`;
  }

  private _tableStudents() {
    if (
      this._students.isLoading ||
      this._selectedRecords.isLoading ||
      !this._selectedRecords.value ||
      this._selectedRecords.value.length === 0 ||
      !this._students.value ||
      this._type.value !== ExportType.US_OS
    ) {
      return nothing;
    }

    const colDefs: ColumnDefinition<typeof Student>[] = [
      {
        columnName: 'Name',
        customID: 'name',
        columnWidth: 'max-content',
        isSticky: true,
        position: 'front',
        selectionSet: { guestAccount: 1, vorname: 1, nachname: 1 },
        getFieldMapping(entityInstance: Student) {
          return `${entityInstance.guestAccount ? '(Gast) ' : ''}${entityInstance.vorname ?? ''} ${
            entityInstance.nachname ?? ''
          }`;
        },
      },
      { columnName: 'Geschlecht', fieldName: 'geschlecht', columnWidth: 'max-content' },
      { columnName: 'Typ', fieldName: 'schuelertyp', columnWidth: 'max-content' },
      {
        columnName: 'Klasse',
        customID: 'class',
        columnWidth: 'max-content',
        selectionSet: { schoolInformation: { jahrgangsstufe: 1, zug: 1 } },
        getFieldMapping(entityInstance: Student) {
          return `${entityInstance.schoolInformation.jahrgangsstufe ?? ' '}${
            entityInstance.schoolInformation.zug ? ` (${entityInstance.schoolInformation.zug})` : ''
          }`;
        },
      },
      // * Dynamically create the colums for all the dates that are present
      ...Array.from(new Set(this._selectedRecords.value.map(r => r.key))).map(key => {
        return {
          columnName: key,
          customID: key,
          columnWidth: 'max-content',
          getFieldMapping: (entityInstance: Student) => {
            const recordsForKey = this._selectedRecordsByKey.value?.get(key) ?? [];
            const recordWithStatus = recordsForKey.find(f =>
              f.studentRecords.find(r => r.userID === entityInstance._id && r.status !== Status.NOT_SURE),
            );

            if (!recordWithStatus) {
              const recordDate = recordsForKey[0]?.date;
              const eintrittsdatum = entityInstance.joinInformation?.eintrittsdatum;
              const austrittsdatum = entityInstance.kuendigung?.austrittsdatum;

              if (recordDate && austrittsdatum && recordDate > austrittsdatum) {
                return '';
              }
              if (recordDate && eintrittsdatum && recordDate < eintrittsdatum) {
                return this._recordMapping(
                  [new UserPresenceRecord({ userID: entityInstance._id, status: Status.NOT_YET_REGISTERED })],
                  entityInstance,
                );
              }

              const assignedToDay = recordsForKey.some(r =>
                r.studentRecords.some(sr => sr.userID === entityInstance._id),
              );

              if (!assignedToDay) {
                return this._recordMapping(
                  [new UserPresenceRecord({ userID: entityInstance._id, status: Status.SCHEDULED_ABSENCE })],
                  entityInstance,
                );
              }
            }

            return this._recordMapping(recordWithStatus ? recordWithStatus.studentRecords : [], entityInstance);
          },
        };
      }),
    ];

    return html`<cr-card space="md" padding="md">
      <chancen-component-card-title>Schüler</chancen-component-card-title>
      <cr-flex flex>
        <x-data-view
          .options=${{
            class: Student,
            onlyShowLastTranslation: true,
            customColumns: colDefs,
            operation: (skip, limit) => {
              return fields => {
                if (skip > 0) {
                  return of([]) as Observable<Student[]>;
                }
                return this._students.observable.pipe(filter(Boolean));
              };
            },
          } satisfies DataViewOptions<typeof Student>}
          flex
          ${css({
            overflow: 'auto',
            maxHeight: 'calc(100vh - 300px)',
            '& th': { position: 'sticky', top: '0', zIndex: '1', backgroundColor: 'white' },
          })}
        >
        </x-data-view>
      </cr-flex>
    </cr-card>`;
  }

  private _tableLekoSchuko() {
    if (
      this._lekoSchuko.isLoading ||
      this._selectedRecords.isLoading ||
      !this._selectedRecords.value ||
      this._selectedRecords.value.length === 0 ||
      !this._lekoSchuko.value ||
      this._type.value !== ExportType.SCHUKO_LEKO
    ) {
      return nothing;
    }

    const colDefs: ColumnDefinition<typeof VerwaltungsMitarbeiter>[] = [
      {
        columnName: 'Name',
        customID: 'name',
        columnWidth: 'max-content',
        isSticky: true,
        position: 'front',
        selectionSet: { vorname: 1, nachname: 1, guestAccount: 1 },
        getFieldMapping(entityInstance: VerwaltungsMitarbeiter) {
          if (!entityInstance.vorname && !entityInstance.nachname) {
            // * Because there are no direct SchuKos at some schools they live under account
            // * without first- / surname. We just use 'SchuKo' then.
            return 'SchuKo';
          }

          const temp = entityInstance._class === TemporaryUser._class;
          const guest = entityInstance instanceof ExternerMitarbeiter && entityInstance.guestAccount;
          return `${temp || guest ? '(Gast) ' : ''}${entityInstance.vorname ?? ''} ${entityInstance.nachname ?? ''}`;
        },
      },
      { columnName: 'Geschlecht', fieldName: 'geschlecht', columnWidth: 'max-content' },
      // * Dynamically create the colums for all the dates that are present
      ...Array.from(new Set(this._selectedRecords.value.map(r => r.key))).map(key => {
        return {
          columnName: key,
          customID: key,
          columnWidth: 'max-content',
          getFieldMapping: (entityInstance: VerwaltungsMitarbeiter) => {
            const recordsForKey = this._selectedRecordsByKey.value?.get(key) ?? [];
            const recordWithStatus = recordsForKey.find(f =>
              f.lekoSchukoRecords.find(r => r.userID === entityInstance._id && r.status !== Status.NOT_SURE),
            );

            if (!recordWithStatus) {
              const assignedToDay = recordsForKey.some(r =>
                r.lekoSchukoRecords.some(sr => sr.userID === entityInstance._id),
              );

              if (!assignedToDay) {
                return this._recordMapping(
                  [new UserPresenceRecord({ userID: entityInstance._id, status: Status.SCHEDULED_ABSENCE })],
                  entityInstance,
                );
              }
            }

            return this._recordMapping(recordWithStatus ? recordWithStatus.lekoSchukoRecords : [], entityInstance);
          },
        };
      }),
    ];

    return html`<cr-card space="md" padding="md">
      <chancen-component-card-title>SchuKo & LeKo</chancen-component-card-title>
      <x-data-view
        flex
        ${css({ overflow: 'auto' })}
        .options=${{
          class: ChancenwerkUser,
          onlyShowLastTranslation: true,
          customColumns: colDefs,
          operation: (skip, limit) => {
            return fields => {
              if (skip > 0) {
                return of([]);
              }
              return of(this._lekoSchuko.value);
            };
          },
        } as DataViewOptions<typeof ChancenwerkUser>}
      >
      </x-data-view>
    </cr-card> `;
  }

  private _buildAttendanceLog(action: string) {
    return new AttendanceExportEventlog({
      kpfID: this._selectedKpf.value?._id || '',
      courseIDs: this._coursesFromKPF.value?.map(course => course._id) ?? [],
      type: this._type.value,
      startDate: this._filter.document?.hasBuTFrom?.toJSDate(),
      endDate: this._filter.document?.hasBuTTill?.toJSDate(),
      action,
    });
  }

  private _exportButton() {
    if (!this._coursesFromKPF.value || this._coursesFromKPF.value.length === 0) {
      return nothing;
    }

    return html`<cr-card padding="md" space="md">
      <chancen-component-card-title>Export</chancen-component-card-title>
      <cr-flex horizontal crossaxis-center space="sm">
        <cr-button
          ${css({ maxWidth: '300px' })}
          icon="save"
          @click=${async (e: CustomEvent) => {
            if (!this._coursesFromKPF.value) return;

            const from = this._filter.document?.hasBuTFrom ?? DateTime.now().startOf('month');
            const till = this._filter.document?.hasBuTTill ?? DateTime.now().endOf('month');

            const button = e.currentTarget as ChromiumButton;
            button.loading = true;
            try {
              switch (this._type.value) {
                case ExportType.US_OS:
                  {
                    const csvs = await PresenceRecord.exportMultipleStudentPresenceRecordsCSV(
                      this._coursesFromKPF.value.map(c => c._id),
                      from,
                      till,
                      this._filter.document?.hasBuT ?? undefined,
                    )();
                    if (csvs.length < 1) {
                      return;
                    }
                    download(csvs[0], `${this._datesFormatted.from}_${this._datesFormatted.to}_Anwesenheit_OS_US.csv`);
                  }
                  break;
                case ExportType.SCHUKO_LEKO:
                  {
                    const csvs = await PresenceRecord.exportMultipleSchuKoLeKoPresenceRecordsCSV(
                      this._coursesFromKPF.value.map(c => c._id),
                      from,
                      till,
                    )();
                    if (csvs.length < 1) {
                      return;
                    }
                    download(
                      csvs[0],
                      `${this._datesFormatted.from}_${this._datesFormatted.to}_Anwesenheit_SchuKo_LeKo.csv`,
                    );
                  }
                  break;
              }

              await AttendanceExportEventlog.safeCreate(this._buildAttendanceLog('Anwesenheit'))();
            } catch (e) {
              logger.error(e);
              void ChromiumSnackbar.show('Beim Export ist etwas schief gelaufen.');
            }

            button.loading = false;
          }}
        >
          Anwesenheit
        </cr-button>

        <cr-button
          ${css({ maxWidth: '380px' })}
          icon="save"
          @click=${async (e: CustomEvent) => {
            if (!this._coursesFromKPF.value) {
              return;
            }

            const from = this._filter.document?.hasBuTFrom ?? DateTime.now().startOf('month');
            const till = this._filter.document?.hasBuTTill ?? DateTime.now().endOf('month');

            const button = e.currentTarget as ChromiumButton;
            button.loading = true;
            try {
              switch (this._type.value) {
                case ExportType.US_OS:
                  {
                    const csvs = await PresenceRecord.exportMultipleStudentPresenceRecordsCSV(
                      this._coursesFromKPF.value.map(c => c._id),
                      from,
                      till,
                      !!this._filter.document?.hasBuT,
                    )();
                    if (csvs.length < 2) {
                      return;
                    }
                    download(
                      csvs[1],
                      from.toFormat('yyyy.dd.MM') +
                        '_' +
                        till.toFormat('yyyy.dd.MM') +
                        '_KommentareEntschuldigung_OS_US.csv',
                    );
                  }
                  break;
                case ExportType.SCHUKO_LEKO:
                  {
                    const csvs = await PresenceRecord.exportMultipleSchuKoLeKoPresenceRecordsCSV(
                      this._coursesFromKPF.value.map(c => c._id),
                      from,
                      till,
                    )();
                    if (csvs.length < 2) {
                      return;
                    }
                    download(
                      csvs[1],
                      from.toFormat('yyyy.dd.MM') +
                        '_' +
                        till.toFormat('yyyy.dd.MM') +
                        '_KommentareEntschuldigung_SchuKo_LeKo.csv',
                    );
                  }
                  break;
              }

              await AttendanceExportEventlog.safeCreate(this._buildAttendanceLog('Kommentare & Entschuldigung'))();
            } catch (e) {
              logger.error(e);
              void ChromiumSnackbar.show('Beim Export ist etwas schief gelaufen.');
            }
            button.loading = false;
          }}
        >
          Kommentare & Entschuldigung
        </cr-button>

        <cr-button
          ${css({ maxWidth: '320px' })}
          icon="save"
          @click=${async (e: CustomEvent) => {
            if (!this._coursesFromKPF.value) {
              return;
            }

            const from = this._filter.document?.hasBuTFrom ?? DateTime.now().startOf('month');
            const till = this._filter.document?.hasBuTTill ?? DateTime.now().endOf('month');

            const button = e.currentTarget as ChromiumButton;
            button.loading = true;
            try {
              let csvs: string[] = [];
              switch (this._type.value) {
                case ExportType.US_OS:
                  csvs = await PresenceRecord.exportMultipleStudentPresenceRecordsCSV(
                    this._coursesFromKPF.value.map(c => c._id),
                    from,
                    till,
                    !!this._filter.document?.hasBuT,
                  )();
                  break;
                case ExportType.SCHUKO_LEKO:
                  csvs = await PresenceRecord.exportMultipleSchuKoLeKoPresenceRecordsCSV(
                    this._coursesFromKPF.value.map(c => c._id),
                    from,
                    till,
                  )();
                  break;
              }

              if (csvs.length < 3) {
                return;
              }

              download(csvs[2], `${this._datesFormatted.from}_${this._datesFormatted.to}_Tageskommentare.csv`);

              await AttendanceExportEventlog.safeCreate(this._buildAttendanceLog('Tageskommentare'))();
            } catch (err) {
              logger.error(err);
              void ChromiumSnackbar.show('Beim Export ist etwas schief gelaufen.');
            }
            button.loading = false;
          }}
        >
          Tageskommentare
        </cr-button>
      </cr-flex>
    </cr-card>`;
  }
}
